import { supabase } from '../lib/supabase';
import { orgStore } from '../lib/orgStore';
import { projectStore } from '../lib/projectStore';
import { requireOrgId, stripImmutable } from '../lib/orgScope';
import type { PrioritizeItem } from '../components/PrioritizeDetailModal';

/**
 * Persists Prioritize items (products / components / features) to Supabase.
 *
 * Storage model: ONE row per item in `prioritize_items`. The few structural /
 * queryable fields live in their own columns; everything else (all sidebar
 * fields + the nested arrays — insights, healthUpdates, releaseNoteEntries,
 * comments, attachments) is stored in a single `data` JSONB column.
 *
 * Direct-Supabase pattern (like documentService): org scoping via orgStore,
 * RLS enforces isolation, methods throw on error — callers wrap in try/catch.
 */

interface PrioritizeRow {
  id: string;
  org_id: string;
  project_id: string | null;
  kind: PrioritizeItem['kind'];
  name: string;
  parent_id: string | null;
  product_parent_id: string | null;
  position: number;
  data: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

// Keys that live in their own columns — everything else goes into `data`.
const COLUMN_KEYS = new Set<keyof PrioritizeItem>([
  'id', 'kind', 'name', 'parentId', 'productParentId',
]);

/** Split a PrioritizeItem into the JSONB `data` blob (omitting column fields). */
function toDataBlob(item: PrioritizeItem): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  (Object.keys(item) as (keyof PrioritizeItem)[]).forEach(k => {
    if (!COLUMN_KEYS.has(k)) data[k] = item[k];
  });
  return data;
}

/** DB row → PrioritizeItem (overlay columns onto the data blob). */
function rowToItem(row: PrioritizeRow): PrioritizeItem {
  return {
    ...(row.data as Partial<PrioritizeItem>),
    id: row.id,
    kind: row.kind,
    name: row.name,
    parentId: row.parent_id,
    productParentId: row.product_parent_id,
    createdAt: (row.data as any)?.createdAt ?? row.created_at,
  } as PrioritizeItem;
}

/** PrioritizeItem → DB insert/update payload (without id / org / created_by). */
function itemToColumns(item: PrioritizeItem) {
  return {
    kind: item.kind,
    name: item.name,
    parent_id: item.parentId ?? null,
    product_parent_id: item.productParentId ?? null,
    data: toDataBlob(item),
    updated_at: new Date().toISOString(),
  };
}

/**
 * Short-lived in-memory cache for getItems. The 5 Product pages (Prioritize,
 * Features, Initiatives, Objectives, Releases) each fetch the entire
 * `prioritize_items` table on mount; without caching, navigating between them
 * re-downloads the full table (incl. the heavy `data` JSONB) every time. The
 * cache lets them share one fetch per project within the TTL. Every write
 * (create/update/delete/reorder) invalidates it so data is never stale.
 */
const PRIORITIZE_CACHE_TTL = 60_000; // 60s
let _itemsCache: { key: string; data: PrioritizeItem[]; expiry: number } | null = null;
const cacheKeyFor = (projectId?: string | null) => projectId ?? '__all__';
function invalidatePrioritizeCache() { _itemsCache = null; }

export const prioritizeService = {
  /** Drop the cached items (call after out-of-band changes if ever needed). */
  invalidateCache: invalidatePrioritizeCache,

  /** Load all items for the current org (optionally scoped to a project). */
  async getItems(projectId?: string | null): Promise<PrioritizeItem[]> {
    const key = cacheKeyFor(projectId);
    const now = Date.now();
    if (_itemsCache && _itemsCache.key === key && now < _itemsCache.expiry) {
      return _itemsCache.data;
    }

    let query = supabase
      .from('prioritize_items')
      .select('*')
      .order('position', { ascending: true })
      .order('created_at', { ascending: true });

    if (projectId) query = query.eq('project_id', projectId);

    const { data, error } = await query;
    if (error) throw error;
    const items = (data as PrioritizeRow[]).map(rowToItem);
    _itemsCache = { key, data: items, expiry: now + PRIORITIZE_CACHE_TTL };
    return items;
  },

  /** Create one item. Returns the saved item with the DB-generated id. */
  async createItem(item: PrioritizeItem, position = 0): Promise<PrioritizeItem> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('You must be signed in to add items.');

    const orgId = orgStore.getOrgId();
    if (!orgId) throw new Error('No organization found.');

    const { data, error } = await supabase
      .from('prioritize_items')
      .insert({
        org_id: orgId,
        project_id: projectStore.getProjectId(),
        ...itemToColumns(item),
        position,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    invalidatePrioritizeCache();
    return rowToItem(data as PrioritizeRow);
  },

  /** Update one item's columns + JSONB blob (saves the full popup detail set).
   *  Selects the row back so an RLS-filtered 0-row update surfaces as an error
   *  instead of a silent no-op. */
  async updateItem(id: string, item: PrioritizeItem): Promise<void> {
    const { data, error } = await supabase
      .from('prioritize_items')
      .update(stripImmutable(itemToColumns(item)))
      .eq('id', id)
      .eq('org_id', requireOrgId())
      .select('id');
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('No matching row updated — the item may not exist or you may not have access (RLS).');
    }
    invalidatePrioritizeCache();
  },

  /** Delete a set of items (an item + its descendants). */
  async deleteItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from('prioritize_items')
      .delete()
      .in('id', ids)
      .eq('org_id', requireOrgId());
    if (error) throw error;
    invalidatePrioritizeCache();
  },

  /**
   * Persist new ordering / parent links after a drag-and-drop reorder.
   * Each update touches only the structural columns of one row.
   */
  async updatePositions(
    updates: Array<{ id: string; position: number; parentId?: string | null; productParentId?: string | null }>,
  ): Promise<void> {
    await Promise.all(
      updates.map(u =>
        supabase
          .from('prioritize_items')
          .update({
            position: u.position,
            parent_id: u.parentId ?? null,
            product_parent_id: u.productParentId ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', u.id)
          .eq('org_id', requireOrgId()),
      ),
    );
    invalidatePrioritizeCache();
  },
};
